import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  useColorModeValue,
  Checkbox,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as SharingUtils from '../utils/sharingUtils';
import { findMommaFuel, requestPasswordReset, requestPasswordResetByEmail } from '../services/AuthService';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    if (password.length < 8) {
      setError('MommaFuel not found.');
      return;
    } else if (!SharingUtils.validateEmail(email)) {
      setError('MommaFuel not found.');
      return;
    }
    
    setIsSubmitting(true);
    setError(''); // Clear any previous errors
    
    try {
      const registryId = await findMommaFuel(email, password);
      
      if (registryId) {
        // Success - redirect to the registry
        navigate(`/registry/${registryId}`,{
            state: { autoAuthenticated: true }
          });
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch (err) {
      setError('Failed to find registry. Please check your credentials and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) {
      toast({
        title: 'Missing Email',
        description: 'Please enter your email address',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!SharingUtils.validateEmail(resetEmail)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsRequestingReset(true);
    
    try {
      // For security, we always show success even if email doesn't exist
      toast({
        title: 'Reset Email Sent',
        description: '',
        status: 'success',
        duration: 5000,
      });
      
      // Call the API but don't show different messages based on result
      const success = await requestPasswordResetByEmail(resetEmail);
      // Reset the forgot password form
      setShowForgotPassword(false);
      setResetEmail('');
      
    } catch (error) {
      console.error('Password reset request error:', error);
      // Still show success message for security
      toast({
        title: 'Reset Email Sent',
        description: '',
        status: 'success',
        duration: 5000,
      });
      setShowForgotPassword(false);
      setResetEmail('');
    } finally {
      setIsRequestingReset(false);
    }
  };

  return (
    <Container maxW="md" py={10}>
      <Box
        bg={bgColor}
        p={6}
        borderRadius="md"
        boxShadow="md"
        border="1px"
        borderColor={borderColor}
      >
        <VStack spacing={6} align="stretch">
          <Box textAlign="center">
            <Heading size="lg" color="mamaFuel.moss.700">
              Find My MommaFuel
            </Heading>
          </Box>
          
          {!showForgotPassword ? (
            <>
              <FormControl isRequired>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setError('')}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setError('')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                />
              </FormControl>

              <Checkbox 
                isChecked={showPassword} 
                alignSelf="flex-start"
                onChange={(e) => setShowPassword(e.target.checked)}
                colorScheme="green"
              >
                Show Password
              </Checkbox>

              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              <Button
                bg="mamaFuel.moss.700"
                color="white"
                _hover={{ bg: "mamaFuel.moss.600" }}
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="Finding MommaFuel..."
                disabled={isSubmitting}
              >
                Find My MommaFuel
              </Button>

              <Button
                variant="outline"
                colorScheme="green"
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot Password?
              </Button>
            </>
          ) : (
            <>
              <Text fontSize="lg" fontWeight="bold" textAlign="center">
                Reset Password
              </Text>
              
              <FormControl isRequired>
                <FormLabel>MommaFuel Email Address</FormLabel>
                <Input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && resetEmail.trim()) {
                      handleForgotPassword();
                    }
                  }}
                />
              </FormControl>

              <HStack spacing={3} width="100%">
                <Button
                  variant="outline"
                  flex={1}
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  bg="mamaFuel.moss.700"
                  color="white"
                  _hover={{ bg: "mamaFuel.moss.600" }}
                  flex={1}
                  onClick={handleForgotPassword}
                  isLoading={isRequestingReset}
                  loadingText="Sending..."
                  isDisabled={!resetEmail.trim()}
                >
                  Send Reset Email
                </Button>
              </HStack>
            </>
          )}

          <Box textAlign="center">
            <Link to="/company-access">
              <Button variant="outline" colorScheme="blue">
                Company Access
              </Button>
            </Link>
          </Box>
          <Box textAlign="center">
            <Link to="/">
              <Button variant="outline" colorScheme="blue">
                Return Home
              </Button>
            </Link>
          </Box>
        </VStack>
      </Box>
    </Container>
  );
};

export default Login;